/**
 * Generic tabs with animated sliding underline (World Cup / Friends / League pattern).
 * `layout="equal"` — segments split container width (World Cup, Friends).
 * `layout="intrinsic"` — packed label widths; horizontal scroll when they overflow the bar.
 * With `intrinsicSpreadWhenFits` (default true), when all labels fit, tabs spread evenly like `equal`.
 */

import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import {
  View,
  Text,
  Pressable,
  Platform,
  Animated,
  Easing,
  LayoutChangeEvent,
  ScrollView,
  type StyleProp,
  type ViewStyle,
} from 'react-native'
import { useTheme } from '../../../src/theme/useTheme'
import { WEB_HOVER_TRANSITION_STYLE } from '../../../src/shared/constants/animations'
import { SoundPressable } from '../../../src/shared/components/SoundPressable/SoundPressable'
import type { SoundId } from '../../../src/shared/constants/sounds.constants'
import { ZportiBadge } from '../zportiBadge'
import { getUnderlineTabBarStyles, type UnderlineTabBarLayoutMode } from './UnderlineTabBar.styles'
import {
  UNDERLINE_TAB_ANIMATION_MS,
  UNDERLINE_TAB_ICON_SIZE_DEFAULT,
} from './UnderlineTabBar.constants'

const DEFAULT_TAB_PADDING_VERTICAL = 10
const DEFAULT_TAB_PADDING_HORIZONTAL = 4
const DEFAULT_TAB_CONTENT_GAP = 5
const DEFAULT_LABEL_FONT_SIZE = 13

export interface UnderlineTabDef<K extends string = string> {
  key: K
  label: string
  Icon?: React.ComponentType<{ size: number; color: string }>
  badgeCount?: number
  disabled?: boolean
  accessibilityLabel?: string
  testID?: string
}

export interface UnderlineTabBarProps<K extends string> {
  tabs: readonly UnderlineTabDef<K>[]
  activeTab: K
  onTabChange: (tab: K) => void
  activeTextColor: string
  inactiveTextColor: string
  underlineColor: string
  /** Lucide icon size when `Icon` is set */
  iconSize?: number
  /** When set, fills the tab row (e.g. World Cup primary shell) */
  backgroundColor?: string
  /** Default true — set false when a parent draws the baseline (e.g. league `tabStrip`) */
  includeBottomBorder?: boolean
  /** Baseline color when `includeBottomBorder` is true */
  bottomBorderColor?: string
  /** Web-only max width for centered chrome */
  webMaxWidth?: number
  /** Primary-style glow under the active segment (World Cup web) */
  underlineWebGlow?: boolean
  style?: StyleProp<ViewStyle>
  /** `equal` = split width; `intrinsic` = packed widths + scroll when needed */
  layout?: UnderlineTabBarLayoutMode
  /**
   * When `layout="intrinsic"`, if the packed row fits the container, use equal-width segments
   * (full-width spread). If it overflows, use horizontal scroll. Set false to always scroll.
   */
  intrinsicSpreadWhenFits?: boolean
  labelFontSize?: number
  tabPaddingVertical?: number
  tabPaddingHorizontal?: number
  tabContentGap?: number
  /** When set, tab presses use `SoundPressable` with this tap id */
  tapSoundId?: SoundId
  /** Root `testID` (Friends toggle, etc.) */
  containerTestID?: string
  showsHorizontalScrollIndicator?: boolean
}

export function UnderlineTabBar<K extends string>({
  tabs,
  activeTab,
  onTabChange,
  activeTextColor,
  inactiveTextColor,
  underlineColor,
  iconSize = UNDERLINE_TAB_ICON_SIZE_DEFAULT,
  backgroundColor,
  includeBottomBorder = true,
  bottomBorderColor: bottomBorderColorProp,
  webMaxWidth,
  underlineWebGlow = false,
  style,
  layout = 'equal',
  intrinsicSpreadWhenFits = true,
  labelFontSize = DEFAULT_LABEL_FONT_SIZE,
  tabPaddingVertical = DEFAULT_TAB_PADDING_VERTICAL,
  tabPaddingHorizontal = DEFAULT_TAB_PADDING_HORIZONTAL,
  tabContentGap = DEFAULT_TAB_CONTENT_GAP,
  tapSoundId,
  containerTestID,
  showsHorizontalScrollIndicator = true,
}: UnderlineTabBarProps<K>): React.ReactElement {
  const theme = useTheme()
  const bottomBorderColor = bottomBorderColorProp ?? theme.gray[200]
  const [containerWidth, setContainerWidth] = useState(0)
  const tabCount = tabs.length

  const [tabLayouts, setTabLayouts] = useState<Array<{ x: number; width: number } | null>>(() =>
    tabs.map(() => null),
  )

  const allTabsMeasured =
    layout === 'intrinsic' &&
    tabLayouts.length === tabCount &&
    tabLayouts.every((l) => l != null && l!.width > 0)

  const packedIntrinsicWidth =
    allTabsMeasured && tabLayouts[tabCount - 1] != null
      ? tabLayouts[tabCount - 1]!.x + tabLayouts[tabCount - 1]!.width
      : null

  const spreadFits =
    layout === 'intrinsic' &&
    intrinsicSpreadWhenFits &&
    containerWidth > 0 &&
    packedIntrinsicWidth != null &&
    packedIntrinsicWidth <= containerWidth + 2

  const displayLayout: UnderlineTabBarLayoutMode = spreadFits ? 'equal' : layout

  const styles = useMemo(
    () =>
      getUnderlineTabBarStyles(theme, {
        containerWidth,
        tabCount,
        includeBottomBorder,
        webMaxWidth,
        layout: displayLayout,
        bottomBorderColor,
        tabPaddingVertical,
        tabPaddingHorizontal,
        tabContentGap,
        labelFontSize,
        activeTextColor,
        inactiveTextColor,
        underlineColor,
        backgroundColor,
        underlineWebGlow,
      }),
    [
      theme,
      containerWidth,
      tabCount,
      includeBottomBorder,
      webMaxWidth,
      displayLayout,
      bottomBorderColor,
      tabPaddingVertical,
      tabPaddingHorizontal,
      tabContentGap,
      labelFontSize,
      activeTextColor,
      inactiveTextColor,
      underlineColor,
      backgroundColor,
      underlineWebGlow,
    ],
  )

  const activeIndex = Math.max(
    0,
    tabs.findIndex((t) => t.key === activeTab),
  )
  const slideValue = useRef(new Animated.Value(activeIndex)).current

  useEffect(() => {
    if (displayLayout !== 'equal') return
    Animated.timing(slideValue, {
      toValue: activeIndex,
      duration: UNDERLINE_TAB_ANIMATION_MS,
      easing: Easing.out(Easing.cubic),
      useNativeDriver: Platform.OS !== 'web',
    }).start()
  }, [activeIndex, displayLayout, slideValue])

  const tabWidth =
    containerWidth > 0 && tabCount > 0 ? containerWidth / tabCount : 0

  const underlineTranslate = useMemo(() => {
    if (displayLayout !== 'equal') return undefined
    if (containerWidth <= 0 || tabCount < 1 || tabWidth <= 0) return undefined
    const inputRange = tabs.map((_, i) => i)
    const outputRange = tabs.map((_, i) => i * tabWidth)
    return {
      transform: [
        {
          translateX: slideValue.interpolate({
            inputRange,
            outputRange,
            extrapolate: 'clamp',
          }),
        },
      ],
    }
  }, [containerWidth, slideValue, tabWidth, tabCount, tabs, displayLayout])

  const onContainerLayout = useCallback((e: LayoutChangeEvent) => {
    const { width } = e.nativeEvent.layout
    if (width > 0) setContainerWidth(width)
  }, [])

  const underlineLeft = useRef(new Animated.Value(0)).current
  const underlineWidth = useRef(new Animated.Value(0)).current
  const intrinsicSeededRef = useRef(false)
  const prevActiveIndexRef = useRef(activeIndex)
  const spreadFitsRef = useRef(false)

  useEffect(() => {
    setTabLayouts((prev) => (prev.length === tabCount ? prev : tabs.map(() => null)))
    if (layout === 'intrinsic') {
      intrinsicSeededRef.current = false
    }
  }, [tabCount, tabs, layout])

  useEffect(() => {
    if (layout !== 'intrinsic') return
    if (spreadFitsRef.current && !spreadFits) {
      setTabLayouts(tabs.map(() => null))
      intrinsicSeededRef.current = false
    }
    spreadFitsRef.current = spreadFits
  }, [spreadFits, layout, tabs, tabCount])

  useLayoutEffect(() => {
    if (layout !== 'intrinsic' || spreadFits) return
    const L = tabLayouts[activeIndex]
    if (!L || L.width <= 0) return

    if (!intrinsicSeededRef.current) {
      underlineLeft.setValue(L.x)
      underlineWidth.setValue(L.width)
      intrinsicSeededRef.current = true
      prevActiveIndexRef.current = activeIndex
      return
    }

    if (prevActiveIndexRef.current !== activeIndex) {
      prevActiveIndexRef.current = activeIndex
      Animated.parallel([
        Animated.timing(underlineLeft, {
          toValue: L.x,
          duration: UNDERLINE_TAB_ANIMATION_MS,
          easing: Easing.out(Easing.cubic),
          useNativeDriver: false,
        }),
        Animated.timing(underlineWidth, {
          toValue: L.width,
          duration: UNDERLINE_TAB_ANIMATION_MS,
          easing: Easing.out(Easing.cubic),
          useNativeDriver: false,
        }),
      ]).start()
    }
  }, [layout, spreadFits, activeIndex, tabLayouts, underlineLeft, underlineWidth])

  const onTabLayout = useCallback(
    (index: number) => (e: LayoutChangeEvent) => {
      if (layout !== 'intrinsic' || spreadFits) return
      const { x, width } = e.nativeEvent.layout
      setTabLayouts((prev) => {
        const base = prev.length === tabCount ? [...prev] : tabs.map(() => null)
        const cur = base[index]
        if (cur != null && cur.x === x && cur.width === width) return prev
        base[index] = { x, width }
        return base
      })
    },
    [layout, spreadFits, tabCount, tabs],
  )

  const renderTab = (tab: UnderlineTabDef<K>, index: number) => {
    const isActive = activeTab === tab.key
    const Icon = tab.Icon
    const disabled = tab.disabled === true
    const iconColor = disabled ? theme.gray[400] : isActive ? activeTextColor : inactiveTextColor
    const badgeCount = tab.badgeCount
    const showBadge = badgeCount != null && badgeCount > 0

    const inner = (
      <View style={styles.tabContent}>
        {Icon != null ? (
          <Icon size={iconSize} color={iconColor} />
        ) : null}
        <Text
          style={[styles.tabText, isActive ? styles.tabTextActive : styles.tabTextInactive]}
          numberOfLines={1}
        >
          {tab.label}
        </Text>
        {showBadge ? (
          <View style={styles.tabBadge}>
            <ZportiBadge
              count={badgeCount}
              variant="notification"
              size="small"
              maxCount={99}
            />
          </View>
        ) : null}
      </View>
    )

    const pressStyle = ({ pressed, hovered }: { pressed: boolean; hovered?: boolean }) => [
      styles.tab,
      Platform.select({ web: WEB_HOVER_TRANSITION_STYLE }),
      disabled && styles.tabDisabled,
      pressed && !disabled && styles.tabPressed,
      hovered && Platform.OS === 'web' && !disabled && styles.tabHover,
    ]

    const fireChange = () => {
      if (disabled) return
      onTabChange(tab.key)
    }

    if (tapSoundId != null) {
      return (
        <SoundPressable
          key={tab.key}
          tapSoundId={tapSoundId}
          playTapSound={!disabled}
          disabled={disabled}
          style={pressStyle}
          onPress={fireChange}
          onLayout={
            layout === 'intrinsic' && !spreadFits ? onTabLayout(index) : undefined
          }
          accessibilityRole="tab"
          accessibilityState={{ selected: isActive, disabled }}
          accessibilityLabel={tab.accessibilityLabel ?? tab.label}
          testID={tab.testID}
        >
          {inner}
        </SoundPressable>
      )
    }

    return (
      <Pressable
        key={tab.key}
        disabled={disabled}
        style={pressStyle}
        onPress={fireChange}
        onLayout={
          layout === 'intrinsic' && !spreadFits ? onTabLayout(index) : undefined
        }
        accessibilityRole="tab"
        accessibilityState={{ selected: isActive, disabled }}
        accessibilityLabel={tab.accessibilityLabel ?? tab.label}
        testID={tab.testID}
      >
        {inner}
      </Pressable>
    )
  }

  const tabRow = <>{tabs.map((tab, i) => renderTab(tab, i))}</>

  if (layout === 'intrinsic' && !spreadFits) {
    return (
      <View
        style={[styles.container, style]}
        onLayout={onContainerLayout}
        testID={containerTestID}
        accessibilityRole="tablist"
      >
        <ScrollView
          horizontal
          nestedScrollEnabled
          showsHorizontalScrollIndicator={showsHorizontalScrollIndicator}
          style={styles.intrinsicScroll}
          contentContainerStyle={styles.intrinsicScrollContent}
          bounces={false}
        >
          <View style={styles.intrinsicInner}>
            <View style={styles.intrinsicTabRow}>{tabRow}</View>
            {tabLayouts[activeIndex] != null ? (
              <Animated.View
                style={[
                  styles.tabUnderlineIntrinsic,
                  { left: underlineLeft, width: underlineWidth },
                ]}
              />
            ) : null}
          </View>
        </ScrollView>
      </View>
    )
  }

  return (
    <View
      style={[styles.container, style]}
      onLayout={onContainerLayout}
      testID={containerTestID}
      accessibilityRole="tablist"
    >
      {tabRow}
      {containerWidth > 0 && underlineTranslate != null && (
        <Animated.View style={[styles.tabUnderlineEqual, underlineTranslate]} />
      )}
    </View>
  )
}
